import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Textarea } from '@/components/ui/textarea';

interface RejectFileDialogProps {
  filename: string;
  isSubmitting: boolean;
  onConfirm: (reviewNote: string | null) => void;
  onCancel: () => void;
}

export function RejectFileDialog({
  filename,
  isSubmitting,
  onConfirm,
  onCancel,
}: RejectFileDialogProps) {
  const { t } = useTranslation(['files', 'common']);
  const [reviewNote, setReviewNote] = useState('');

  return (
    <ModalOverlay onRequestClose={onCancel}>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-zooerrorbg">
          <Icon name="X" className="text-xl text-zooerrortext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">
          {t('files:rejectDialog.title', { filename })}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5">{t('files:rejectDialog.hint')}</p>

        <div className="space-y-1.5 mt-4">
          <Label htmlFor="file-reject-note">{t('files:rejectDialog.reasonLabel')}</Label>
          <Textarea
            id="file-reject-note"
            rows={3}
            value={reviewNote}
            placeholder={t('files:rejectDialog.reasonPlaceholder')}
            onChange={function update(changeEvent) {
              setReviewNote(changeEvent.target.value);
            }}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            isLoading={isSubmitting}
            onClick={function confirm() {
              const trimmedNote = reviewNote.trim();
              onConfirm(trimmedNote.length === 0 ? null : trimmedNote);
            }}
          >
            {t('files:rejectDialog.confirm')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
